import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { appCurrentBranding } from '../../../config/branding';
import { branding } from '../../../config/enum';
import LanguageSelector from '../../../contexts/language-selector/LanguageSelector';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import { setCookie } from '../../../shared-functions/Cookies';
import { permissionList } from '../../../shared-functions/JWTDocodeFunction';
import { setLocalStorageItem } from '../../../shared-functions/LocalStorage';
import { emailValidation, phoneNumberValidation } from '../../../shared-functions/Validation';
import authApi from '../../../utils/api/auth';

/**
 * Login page function component
 */
function LoginComponent() {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [credentials, setCredentials] = useState<{
    emailOrPhoneNumber: string;
    password: string;
    type: string | number;
    countryCode: string;
  }>({
    emailOrPhoneNumber: '',
    password: '',
    type: 'email',
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
  });
  const [submitOpen, setSubmitOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  /**
   * Input field change function
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
  };

  /**
   * handle submit function
   */
  const submitFunction = () => {
    setSubmitOpen(true);
    if (
      credentials.countryCode !== '' &&
      (emailValidation(credentials.emailOrPhoneNumber) ||
        phoneNumberValidation(credentials.emailOrPhoneNumber))
    ) {
      setDisabled(true);
      const request: {
        password: string;
        emailOrPhoneNumber: string;
        countryCode?: string;
      } = {
        password: credentials.password,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
      };
      if (phoneNumberValidation(credentials.emailOrPhoneNumber)) {
        request.countryCode = credentials.countryCode;
      }
      authApi
        .login(request)
        .then((response) => {
          const responseData = response?.data;

          // Store access token inside cookies
          setCookie(
            'ev-access-token',
            responseData?.access?.token,
            null,
            responseData?.access?.expires,
          );

          // Store refresh token inside cookies
          setCookie(
            'ev-refresh-token',
            responseData?.refresh?.token,
            null,
            responseData?.refresh?.expires,
          );

          const userVerification = {
            isEmailVerified: responseData?.isEmailVerified,
            isPhoneNumberVerified: responseData?.isPhoneNumberVerified,
          };

          // Set verification in local storage to use in email verification page
          setLocalStorageItem('userVerification', userVerification);
          setDisabled(false);

          const permissions = permissionList();

          // CASE 1 → If dashboard is inside the permission array
          if (permissions.includes('dashboard')) {
            navigate('/ev-admin/dashboard');
            return;
          }

          permissions.some((perm) => {
            switch (perm) {
              case 'accounts_management':
                navigate('/ev-admin/account');
                return true;

              case 'device_management':
                navigate('/ev-admin/device-management');
                return true;

              case 'charging_station':
                navigate('/ev-admin/charging-station/approved');
                return true;

              case 'dynamic_pricing':
                navigate('/ev-admin/dynamic-pricing/approved');
                return true;

              case 'user_management':
                navigate('/ev-admin/user-management/user');
                return true;

              case 'end_consumer_management':
                navigate('/ev-admin/end-consumer');
                return true;

              case 'payments':
                navigate('/ev-admin/payments');
                return true;

              case 'wallet_revenue_sharing':
                navigate('/ev-admin/revenue/sharing');
                return true;

              case 'reporting_tools':
                navigate('/ev-admin/reports/energyReport');
                return true;

              default:
                return false; // continue checking
            }
          });

          // navigate('../ev-admin/account');
        })
        .catch((error) => {
          setDisabled(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  return (
    <div className="login-component-v2" dir={i18n.dir()}>
      <div className="login-component-v2-header">
        <p className="title">{t('signIn')}</p>
      </div>
      <div className="login-component-v2-body">
        <RadioButton
          name="type"
          value={credentials.type}
          list={[
            {
              label: `${t('email')}`,
              value: 'email',
            },
            {
              label: `${t('phoneNumber')}`,
              value: 'phone',
            },
          ]}
          onChangeValue={handleCredentialChange}
        />
        {credentials.type === 'email' ? (
          <CustomInput
            type="email"
            placeholder={t('enterEmail')}
            name="emailOrPhoneNumber"
            value={credentials.emailOrPhoneNumber}
            onChangeValue={handleCredentialChange}
            error={submitOpen && !emailValidation(credentials.emailOrPhoneNumber)}
            errorMessage={t('emailInvalid')}
          />
        ) : (
          <CustomPhoneInput
            type="number"
            placeholder={t('enterPhoneNumber')}
            name="emailOrPhoneNumber"
            value={credentials.emailOrPhoneNumber}
            value1={credentials.countryCode}
            onChangeValue={handleCredentialChange}
            error={submitOpen && !phoneNumberValidation(credentials.emailOrPhoneNumber)}
            errorMessage={t('phoneInvalid')}
          />
        )}
        <div className="password-container">
          <CustomInput
            inputLabel={t('password')}
            type="password"
            placeholder={t('enterPassword')}
            name="password"
            value={credentials.password}
            onChangeValue={handleCredentialChange}
          />
        </div>
        <div className="forgot-password">
          <span role="presentation" onClick={() => navigate('../login/forgot-password')}>
            {t('forgotPassword')}
          </span>
        </div>
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={submitFunction}
          disabled={disabled}
        />
      </div>
      <div className="language-selector">
        <LanguageSelector isCollapsed={false} />
      </div>
    </div>
  );
}

export default LoginComponent;
